import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import Link from 'next/link';
import ReCAPTCHA from 'react-google-recaptcha';

import { getInternalPath, IgetInternalPath } from '@helpers/internalLink';

import Button from '@components/core/Button';
import Input from '@components/core/Input';
import Dropdown from '@components/core/Dropdown';
import Grid from '@components/core/layout/Grid';
import Icon from '@components/core/Icon';
import { fetchKinderopvangLocationsNames } from '@services/location';

import { notEmpty, isEmail, isPhoneNumber } from '@helpers/string';

const styling = {
  errorMessage: `
    text-body-sm
    text-left
    text-error
  `,
  checkbox: `
    peer
    block
    appearance-none
    cursor-pointer
    rounded-sm
    w-5
    h-5
    min-w-5
    border
    border-shade-300

    checked:bg-primary
    checked:border-primary
    hover:border-primary-300
    hover:bg-primary-50
  `,
  dateInput: `
    w-full
    h-12
    px-6
    py-3
    border
    border-shade-100
    rounded-full
    text-body-lg
    outline-0
    bg-white

    hover:border-primary-300
    active:border-primary
    focus:border-primary
  `,
  dayButton: `
    px-4
    py-2
    text-body-md
    rounded-full
    border
    border-shade-300
    cursor-pointer
    transition-colors
    hover:border-primary-300
    hover:bg-primary-50
  `,
  dayButtonActive: `
    bg-primary
    text-white
    border-primary
    hover:bg-primary
    hover:border-primary
  `,
};

const days = [
  'Maandag',
  'Dinsdag',
  'Woensdag',
  'Donderdag',
  'Vrijdag',
];

interface IVoorinschrijvingForm {
  title: string,
  intro: string,
  privacyLink: {
    externalUrl?: string,
    internalLink?: IgetInternalPath,
  },
  termsLink: {
    externalUrl?: string,
    internalLink?: IgetInternalPath,
  },
  emailSubject: string,
  recipientEmail: string,
  submitButtonLabel: string,
  locale: string,
  responseBody: string,
  responseTitle: string
}

interface ILocation {
  pageTitle: string,
  locationCode?: string,
  cities: string[],
}

export default function VoorinschrijvingForm({
  title,
  intro,
  privacyLink,
  termsLink,
  emailSubject,
  recipientEmail,
  submitButtonLabel,
  responseBody,
  responseTitle,
}: IVoorinschrijvingForm) {

  const defaultFieldState = {
    value: '',
    isValid: false,
    validState: {}, // key: type of validation, value: boolean
    showErrorMessage: false,
  };

  const [ formState, setFormState ] = useState<{[key: string]: any}>({
    city: { ...defaultFieldState, value: null },
    location: { ...defaultFieldState, value: null },
    childName: { ...defaultFieldState },
    birthDate: { ...defaultFieldState },
    preferredDays: { ...defaultFieldState, value: [] },
    firstName: { ...defaultFieldState },
    preposition: { ...defaultFieldState },
    lastName: { ...defaultFieldState },
    email: { ...defaultFieldState },
    phone: { ...defaultFieldState },
    openQuestion: { ...defaultFieldState },
    termsAccepted: {
      value: '',
      isValid: false,
      showErrorMessage: false,
    },
    recaptcha: {
      value: '',
      isValid: false,
      showErrorMessage: false,
    },
  });

  const [ loading, setLoading ] = useState(false);
  const [ submitted, setSubmitted ] = useState(false);
  const [ formErrorMessage, setFormErrorMessage ] = useState('');
  const [ locations, setLocations ] = useState<ILocation[]>([]);

  const termsLinkHref = termsLink?.externalUrl
    ?? (termsLink?.internalLink ? getInternalPath(termsLink.internalLink) : undefined);
  const privacyLinkHref = privacyLink?.externalUrl
    ?? (privacyLink?.internalLink ? getInternalPath(privacyLink.internalLink) : undefined);

  useEffect(() => {
    fetchKinderopvangLocationsNames(false).then((result) => {
      setLocations(result.map((location) => ({
        pageTitle: location.pageTitle,
        locationCode: location.locationCode,
        cities: Array.from(new Set<string>(
          (location.products ?? [])
            .map((product) => product?.city?.trim())
            .filter(Boolean)
        )),
      })));
    });
  }, []);

  const cityOptions = useMemo(() => {
    return Array.from(new Set(locations.flatMap((location) => location.cities)))
      .sort((a, b) => a.localeCompare(b))
      .map((city) => ({ label: city, value: city }));
  }, [ locations ]);

  const locationOptions = useMemo(() => {
    const selectedCity = formState.city.value?.value;

    return locations
      .filter((location) => !selectedCity || location.cities.includes(selectedCity))
      .sort((a, b) => a.pageTitle.localeCompare(b.pageTitle))
      .map((location) => ({ label: location.pageTitle, value: location.pageTitle }));
  }, [ locations, formState.city.value ]);

  const submitVoorinschrijvingForm = useCallback((e: React.FormEvent) => {
    e.preventDefault();
    const formElement = (e.target as HTMLFormElement);

    if (loading) {
      return;
    }

    let errorFound = false;

    for (const fieldName in formState) {
      if (!formState[fieldName].isValid) {
        setFormState((prevState: typeof formState) => {
          const nextState = Object.assign({}, prevState);

          nextState[fieldName].showErrorMessage = true;

          return nextState;
        });

        errorFound = true;
      }
    }

    if (errorFound) {
      setTimeout(() => {
        const errorElement: HTMLElement|null = formElement.querySelector('[data-error="true"]');

        if (errorElement) {
          errorElement.focus();
        }
      }, 50);

      return;
    }

    const selectedLocation = locations.find((location) => location.pageTitle === formState.location.value?.value);

    const data = {
      city: formState.city.value?.value,
      location: formState.location.value?.value,
      locationCode: selectedLocation?.locationCode,
      childName: formState.childName.value,
      birthDate: formState.birthDate.value,
      preferredDays: days.filter((day) => formState.preferredDays.value.includes(day)),
      firstName: formState.firstName.value,
      lastName: formState.lastName.value,
      preposition: formState.preposition.value,
      openQuestion: formState.openQuestion.value,
      email: formState.email.value,
      phone: formState.phone.value,
      recipientEmail: recipientEmail,
      emailSubject: emailSubject,
      formLocation: window.location.href,
      recaptcha: formState.recaptcha.value,
    };

    setLoading(true);
    setFormErrorMessage('');

    fetch('/api/voorinschrijving-form-email', {
      method: 'POST',
      headers: {
        'Accept': 'application/json, text/plain, */*',
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(data),
    })
      .then((res) => {
      // Response received
        setLoading(false);

        if (res.status === 200 || res.status === 201) {
        // Response succeeded!
          setSubmitted(true);
          return;
        }

        return res.text();
      })
      .then(errorMessage => {
        setFormErrorMessage('Fout tijdens het verzenden. Probeer het opnieuw!');
        console.error(errorMessage);
      })
      .catch(error => {
        setFormErrorMessage('Fout tijdens het verzenden. Probeer het opnieuw!');
        console.error(error);
      });

  }, [ loading, formState, locations, recipientEmail, emailSubject ]);

  const updateFormState = (fieldName: keyof typeof formState, key: 'value'|'isValid'|'validState'|'showErrorMessage', value: any) => {
    setFormState((prevState: typeof formState) => {
      const nextState = Object.assign({}, prevState);

      // Allow state to be changed for all keys
      nextState[fieldName][key] = value;

      // Clear error message after changing value
      if (key === 'value') {
        nextState[fieldName]['errorMessage'] = '';
      }

      return nextState;
    });
  };

  const handleEmitOnChange = (e: ChangeEvent, fieldName: keyof typeof formState) => {
    updateFormState(fieldName, 'value', (e.target as HTMLInputElement).value);
    updateFormState(fieldName, 'showErrorMessage', false);
  };

  const handleEmitValidate = (validState: { [key: string]: boolean }, fieldName: keyof typeof formState) => {
    let isValid = true;
    for (const validateKey in validState) {
      if (!validState[validateKey]) {
        isValid = false;
        break;
      }
    }
    updateFormState(fieldName, 'isValid', isValid);
    updateFormState(fieldName, 'validState', validState);
  };

  const handleCityChange = (selected?: { label: string, value: string }) => {
    updateFormState('city', 'value', selected);
    updateFormState('city', 'isValid', !!selected?.value);
    updateFormState('city', 'showErrorMessage', !selected?.value);

    // Reset the location when it is not in the newly selected city
    const selectedLocation = locations.find((location) => location.pageTitle === formState.location.value?.value);
    if (selectedLocation && selected?.value && !selectedLocation.cities.includes(selected.value)) {
      updateFormState('location', 'value', null);
      updateFormState('location', 'isValid', false);
    }
  };

  const handleLocationChange = (selected?: { label: string, value: string }) => {
    updateFormState('location', 'value', selected);
    updateFormState('location', 'isValid', !!selected?.value);
    updateFormState('location', 'showErrorMessage', !selected?.value);

    // Fill in the city when the location is only in one city
    const selectedLocation = locations.find((location) => location.pageTitle === selected?.value);
    if (!formState.city.value && selectedLocation?.cities.length === 1) {
      updateFormState('city', 'value', { label: selectedLocation.cities[0], value: selectedLocation.cities[0] });
      updateFormState('city', 'isValid', true);
      updateFormState('city', 'showErrorMessage', false);
    }
  };

  const toggleDay = (day: string) => {
    const preferredDays: string[] = formState.preferredDays.value.includes(day)
      ? formState.preferredDays.value.filter((preferredDay) => preferredDay !== day)
      : [ ...formState.preferredDays.value, day ];

    updateFormState('preferredDays', 'value', preferredDays);
    updateFormState('preferredDays', 'isValid', preferredDays.length > 0);
    updateFormState('preferredDays', 'showErrorMessage', false);
  };

  return (
    <Grid>
      <div className='col-span-12 md:col-span-8 md:col-start-3'>
        {
          submitted && !loading &&
          <div id='voorinschrijving-formulier-succes'>
            <h2 className='text-heading-md md:text-heading-lg mb-4'>{ responseTitle }</h2>
            <div className='text-body-lg md:text-body-xl mb-6 md:mb-8 xl:mb-12'>{ responseBody }</div>
          </div>
        }
        {
          !submitted &&
          <div>
            <h2 className='text-heading-md md:text-heading-lg mb-4'>{ title }</h2>
            <div className='text-body-lg md:text-body-xl mb-6 md:mb-8 xl:mb-12'>{ intro }</div>
          </div>
        }
        { !submitted && (
          <form onSubmit={ submitVoorinschrijvingForm } className={ loading ? 'opacity-50 pointer-events-none' : '' } id='voorinschrijving-formulier'>
            <div className='flex flex-col gap-y-4'>
              <h3 className='text-heading-xs md:text-heading-sm'>Locatie</h3>
              <div className='flex-col flex md:flex-row gap-4'>
                <Dropdown
                  id='voorinschrijving-city'
                  label='Plaats locatie'
                  isSearchable
                  options={ cityOptions }
                  value={ formState.city.value }
                  onChange={ handleCityChange }
                  placeholder='-Kies plaats-'
                  errorMessage='Selecteer een plaats'
                  showErrorMessage={ formState.city.showErrorMessage }
                  className='grow basis-0 z-40'
                />
                <Dropdown
                  id='voorinschrijving-location'
                  label='Naam locatie'
                  isSearchable
                  options={ locationOptions }
                  value={ formState.location.value }
                  onChange={ handleLocationChange }
                  placeholder='-Kies locatie-'
                  errorMessage='Selecteer een locatie'
                  showErrorMessage={ formState.location.showErrorMessage }
                  className='grow basis-0 z-30'
                />
              </div>

              <h3 className='text-heading-xs md:text-heading-sm mt-2 md:mt-4'>Gegevens kind</h3>
              <div className='flex-col flex md:flex-row gap-x-4'>
                <Input
                  label='Naam van je kind'
                  required
                  value={ formState.childName.value }
                  isValid={ formState.childName.isValid }
                  showErrorMessage={ formState.childName.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'childName') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'childName') }
                  validState={ formState.childName.validState }
                  validation={ {
                    required: {
                      errorMessage: 'Je hebt nog geen naam van je kind ingevuld',
                    },
                  } }
                  customStyling={ {
                    container: {
                      className: 'grow basis-0 mb-4 md:mb-0',
                    },
                  } }
                />
                <div className='grow basis-0'>
                  <div className='flex items-center mb-1'>
                    <label htmlFor='voorinschrijving-birth-date' className='text-body-md text-body-md--semibold'>
                      Geboortedatum van je kind
                    </label>
                  </div>
                  <input
                    type='date'
                    id='voorinschrijving-birth-date'
                    value={ formState.birthDate.value }
                    onChange={ (e) => {
                      updateFormState('birthDate', 'value', e.target.value);
                      updateFormState('birthDate', 'isValid', !!e.target.value);
                      updateFormState('birthDate', 'showErrorMessage', false);
                    } }
                    className={ `${ styling.dateInput } ${ formState.birthDate.showErrorMessage && !formState.birthDate.isValid ? 'border-error' : '' }` }
                    data-error={ formState.birthDate.showErrorMessage && !formState.birthDate.isValid ? 'true' : undefined }
                  />
                  { formState.birthDate.showErrorMessage && !formState.birthDate.isValid && (
                    <span className={ styling.errorMessage }>Je hebt nog geen geboortedatum ingevuld</span>
                  ) }
                </div>
              </div>
              <div>
                <div className='flex items-center mb-1'>
                  <span className='text-body-md text-body-md--semibold'>Voorkeursdagen</span>
                </div>
                <div className='flex flex-wrap gap-2'>
                  { days.map((day) => {
                    const active = formState.preferredDays.value.includes(day);

                    return (
                      <button
                        key={ day }
                        type='button'
                        onClick={ () => toggleDay(day) }
                        className={ `${ styling.dayButton } ${ active ? styling.dayButtonActive : '' }` }
                        aria-pressed={ active }
                      >
                        { day }
                      </button>
                    );
                  }) }
                </div>
                { formState.preferredDays.showErrorMessage && !formState.preferredDays.isValid && (
                  <span className={ styling.errorMessage }>Kies minimaal één voorkeursdag</span>
                ) }
              </div>

              <h3 className='text-heading-xs md:text-heading-sm mt-2 md:mt-4'>Jouw gegevens</h3>
              <Input
                label='Voornaam'
                required
                value={ formState.firstName.value }
                isValid={ formState.firstName.isValid }
                showErrorMessage={ formState.firstName.showErrorMessage }
                emitOnChange={ (e) => handleEmitOnChange(e, 'firstName') }
                emitOnValidate={ (e) => handleEmitValidate(e, 'firstName') }
                validState={ formState.firstName.validState }
                validation={ {
                  required: {
                    errorMessage: 'Je hebt nog geen naam ingevuld',
                  },
                } }
                customStyling={ {
                  container: {
                    className: 'grow',
                  },
                } }
              />
              <div className='flex-col flex md:flex-row gap-x-4'>
                <Input
                  label='Tussenvoegsel'
                  value={ formState.preposition.value }
                  isValid={ formState.preposition.isValid }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'preposition') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'preposition') }
                  validState={ formState.preposition.validState }
                  customStyling={ {
                    container: {
                      className: 'md:max-w-[165px] mb-4 md:mb-0',
                    },
                  } }
                />
                <Input
                  label='Achternaam'
                  required
                  value={ formState.lastName.value }
                  isValid={ formState.lastName.isValid }
                  showErrorMessage={ formState.lastName.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'lastName') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'lastName') }
                  validState={ formState.lastName.validState }
                  validation={ {
                    required: {
                      errorMessage: 'Je hebt nog geen achternaam ingevuld',
                    },
                  } }
                  customStyling={ {
                    container: {
                      className: 'grow',
                    },
                  } }
                />
              </div>
              <div className='flex-col flex md:flex-row gap-x-4'>
                <Input
                  label='E-mailadres'
                  required
                  value={ formState.email.value }
                  isValid={ formState.email.isValid }
                  showErrorMessage={ formState.email.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'email') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'email') }
                  validState={ formState.email.validState }
                  validation={ {
                    required: {
                      validateFunction: notEmpty,
                      errorMessage: 'Je hebt nog geen e-mailadres ingevuld',
                    },
                    pattern: {
                      validateFunction: isEmail,
                      errorMessage: 'Je hebt een ongeldig e-mailadres ingevuld. Kun je het controleren?',
                    },
                  } }
                  customStyling={ {
                    container: {
                      className: 'grow mb-4 md:mb-0',
                    },
                  } }
                />
                <Input
                  label='Telefoonnummer'
                  required
                  value={ formState.phone.value }
                  isValid={ formState.phone.isValid }
                  showErrorMessage={ formState.phone.showErrorMessage }
                  emitOnChange={ (e) => handleEmitOnChange(e, 'phone') }
                  emitOnValidate={ (e) => handleEmitValidate(e, 'phone') }
                  validState={ formState.phone.validState }
                  validation={ {
                    required: {
                      errorMessage: 'Je hebt nog geen telefoonnummer ingevuld',
                    },
                    pattern: {
                      validateFunction: isPhoneNumber,
                      errorMessage: 'Dit is geen geldig telefoonnummer. Kun je het controleren?',
                    },
                  } }
                  placeholder='bv: 0101234567/0612345678'
                  customStyling={ {
                    container: {
                      className: 'grow',
                    },
                  } }
                />
              </div>
              <Input
                type='textarea'
                placeholder='Stel hier uw vraag...'
                label='Stel hier je vraag'
                value={ formState.openQuestion.value }
                isValid={ formState.openQuestion.isValid }
                emitOnChange={ (e) => handleEmitOnChange(e, 'openQuestion') }
                emitOnValidate={ (e) => handleEmitValidate(e, 'openQuestion') }
                validState={ formState.openQuestion.validState }
                customStyling={ {
                  container: {
                    className: 'grow mt-2',
                  },
                } }
              />
              <div>
                <div className='flex items-center mt-2 relative'>
                  <input
                    type='checkbox'
                    id='voorinschrijvingTermsAccepted'
                    checked={ !!formState.termsAccepted.value }
                    className={ styling.checkbox + ' flex-none' }
                    onChange={ (e) => {
                      updateFormState('termsAccepted', 'value', e.target.checked);
                      updateFormState('termsAccepted', 'isValid', e.target.checked);
                      updateFormState('termsAccepted', 'showErrorMessage', false);
                    } }
                  />
                  { !!formState.termsAccepted.value && (
                    <span className='absolute left-[3px] pointer-events-none peer-checked:text-white peer-hover:text-primary'>
                      <Icon name='Done' className='!w-[13.58px]' />
                    </span>
                  ) }
                  <label
                    htmlFor='voorinschrijvingTermsAccepted'
                    className='pl-4 cursor-pointer select-none'
                  >
                    Ik ga akkoord met de{ ' ' }
                    <Link
                      href={ privacyLinkHref }
                      className='text-primary underline'
                      target='_blank'>
                      Privacyverklaring
                    </Link>
                    <span>en </span>
                    <Link
                      href={ termsLinkHref }
                      className='text-primary underline whitespace-nowrap'
                      target='_blank'>
                      Algemene voorwaarden.
                    </Link>
                  </label>
                </div>
                { !formState.termsAccepted.isValid && formState.termsAccepted.showErrorMessage && (
                  <span className={ styling.errorMessage }>Je hebt nog geen toestemming gegeven</span>
                ) }
              </div>
              <div className='relative z-10 mt-8 mb-2 md:mb-4 xl:mb-8'>
                <ReCAPTCHA
                  sitekey={ process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY }
                  onChange={ (captchaCode) => {
                    if(!captchaCode) {
                      updateFormState('recaptcha', 'value', false);
                      updateFormState('recaptcha', 'isValid', false);
                      return;
                    }
                    updateFormState('recaptcha', 'value', captchaCode);
                    updateFormState('recaptcha', 'isValid', true);
                  } }
                  onExpired={ () => {
                    updateFormState('recaptcha', 'value', false);
                    updateFormState('recaptcha', 'isValid', false);
                  } }
                />
                { !formState.recaptcha.isValid && formState.recaptcha.showErrorMessage && (
                  <span className={ styling.errorMessage }>Controleer of je geen robot bent.</span>
                ) }
              </div>
              <div>
                <Button disabled={ loading }>
                  <span>{ submitButtonLabel }</span>
                </Button>
                { formErrorMessage && (
                  <span className={ `${ styling.errorMessage } md:w-1/2 ml-2` }>Fout tijdens het verzenden. Probeer het opnieuw!</span>
                ) }
              </div>
            </div>
          </form>
        ) }
      </div>
    </Grid>
  );
}
